// ─── «📝 I miei dati» — nome, cognome e numero cambiati dall'assistente ─────
//
// 🗣️ 29/09/2026, mockup «I miei dati» approvato (*«ok procedi»*, scelte
// consigliate 1A · 2A · 3A — regolamento B1 · B2 · B3):
//   1A · il socio cambia nome e cognome DA SOLO: scrive, rilegge, conferma;
//   2A · un numero che sta già sulla scheda di un altro socio NON cambia: la
//        richiesta va da sola in «💬 Segreteria», con le due schede;
//   3A · lo «🕘 Storico dei dati» della scheda mostra anche le modifiche della
//        Segreteria, lette dal registro (`activity_log`), senza tabelle nuove.
//
// ⭐ Il gestionale decide e registra; il bot riferisce l'esito.
import { z } from 'zod';
import { cognomeRegistrazioneSchema, nomeRegistrazioneSchema } from './registrazione.js';
/** Quello che l'assistente mostra in «📝 I miei dati». */
export const datiDelSocioSchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    /** In forma internazionale (`+39…`); `null` se la scheda non ne ha. */
    phone: z.string().nullable(),
    /**
     * 🪪 13/10/2026 («Cognome puntato», 1A): il socio ha scelto che gli altri
     * giocatori vedano il suo nome intero. Falso ⇒ vedono «Luca B.».
     */
    fullNamePublic: z.boolean().optional(),
    /**
     * 🛡️ 22/10/2026 («La cancellazione chiesta dal socio», A): quando ha chiesto dal bot di cancellare i suoi
     * dati, finché la Segreteria non risponde. `null` se non l'ha chiesta.
     */
    erasureRequestedAt: z.string().nullable().optional(),
});
/** Il corpo di `POST /consumer/players/:memberId/full-name-public`. */
export const domandaNomeInteroDelPonteSchema = z.object({ visible: z.boolean() });
/**
 * Il corpo di `POST /consumer/players/:memberId/name`: UN campo alla volta,
 * come nel bot («✏️ Cambia il nome» · «✏️ Cambia il cognome»).
 *
 * ⚠️ La rotta lo legge A MANO con `nomeDalPonte`: un nome che non va bene
 * torna `rifiutato` con la frase per il socio, non un `400`.
 */
export const domandaNomeDelPonteSchema = z.object({
    firstName: z.string().max(200).optional(),
    lastName: z.string().max(200).optional(),
});
/**
 * Il nome o il cognome nuovo, con le regole della registrazione: le stesse
 * lettere, lo stesso limite di 40, le maiuscole rimesse allo stesso modo.
 */
export function nomeDalPonte(corpo) {
    const campi = ['firstName', 'lastName'].filter((c) => corpo[c] !== undefined);
    if (campi.length !== 1)
        return { ok: false, frase: 'Scrivimi il nome oppure il cognome, uno alla volta.' };
    const campo = campi[0];
    const schema = campo === 'firstName' ? nomeRegistrazioneSchema : cognomeRegistrazioneSchema;
    const letto = schema.safeParse(corpo[campo]);
    if (!letto.success)
        return { ok: false, frase: letto.error.issues[0].message };
    return { ok: true, campo, valore: letto.data };
}
/** Il corpo di `POST /consumer/players/:memberId/phone`. */
export const domandaNumeroDelPonteSchema = z.object({
    /** Il numero condiviso col tasto di Telegram, com'è arrivato. */
    phone: z.string().trim().min(3).max(40),
    /** Per la richiesta in «💬 Segreteria»: la stessa chiave non la scrive due volte. */
    idempotencyKey: z.string().min(8).max(100),
});
/**
 * ⚖️ Due verdetti, come ogni scrittura del ponte — `fatto` e `rifiutato` sono le
 * sole parole che il bot prende per un'affermazione — e dentro `fatto` com'è
 * andata (`result`):
 * - `cambiato` — cambiato, e registrato;
 * - `gia_cosi` — era già così: niente da scrivere;
 * - `in_segreteria` — il numero sta sulla scheda di un altro socio (2A): NON
 *   cambia, e la richiesta è già in «💬 Segreteria». Il nome dell'altro socio
 *   non esce verso l'assistente.
 * `rifiutato` — la forma non va: `reason` dice come scriverlo.
 */
export const RISULTATI_I_MIEI_DATI = ['cambiato', 'gia_cosi', 'in_segreteria'];
export const esitoIMieiDatiSchema = z.object({
    outcome: z.enum(['fatto', 'rifiutato']),
    result: z.enum(RISULTATI_I_MIEI_DATI).nullable(),
    reason: z.string().nullable(),
    /** I dati come sono DOPO la richiesta: quelli da mostrare al socio. */
    details: datiDelSocioSchema.nullable(),
});
// ─── 🕘 Lo storico dei dati, nella scheda del socio ──────────────────────────
export const CAMPI_DELLO_STORICO = ['firstName', 'lastName', 'phone', 'fullNamePublic', 'gender'];
export const ETICHETTE_CAMPI_DELLO_STORICO = {
    firstName: 'Nome',
    lastName: 'Cognome',
    phone: 'Numero',
    fullNamePublic: 'Nome per gli altri giocatori',
    // 🙋 16/10/2026: dalla Segreteria, o dal bot coi tipi di partita scelti dal socio.
    gender: 'Sesso',
};
/** Le azioni del registro che possono toccare nome, cognome e numero. */
export const AZIONI_DELLO_STORICO = [
    'member.create',
    'member.create.bot',
    'member.update',
    'member.name.bot',
    'member.phone.bot',
    'member.full_name_public.bot',
    'member.gender.bot',
];
export const rigaStoricoDatiSchema = z.object({
    at: z.string(),
    field: z.enum(CAMPI_DELLO_STORICO),
    /** `null` = vuoto, o non registrato (gli import non scrivono il «prima»). */
    before: z.string().nullable(),
    after: z.string().nullable(),
    /** Chi: il bot «Padel Village su Telegram» (per conto del socio), la Segreteria, o un passaggio senza persona. */
    by: z.enum(['assistente', 'segreteria', 'altro']),
    /** Il nome di chi della Segreteria; `null` per gli altri due. */
    actorName: z.string().nullable(),
});
/** 🙋 Il sesso, detto a parole come nella scheda. */
const SESSO_A_PAROLE = { M: 'Uomo', F: 'Donna' };
const comeTesto = (v) => v === undefined || v === null || v === ''
    ? null
    : // 🪪 La scelta del nome per gli altri (13/10/2026): detta a parole.
        v === true
            ? 'intero'
            : v === false
                ? 'col cognome puntato'
                : String(v);
/**
 * Dal registro alle righe dello storico: un campo per riga, e SOLO quelli che
 * sono cambiati davvero.
 *
 * ⚠️ Il PATCH della scheda registra come «prima» ogni chiave del corpo, anche
 * quelle rimaste uguali (`soloCambiati`): senza il confronto, ogni salvataggio
 * della scheda riempirebbe lo storico di «Luca → Luca».
 */
export function righeDelloStorico(registro) {
    const righe = [];
    for (const r of registro) {
        if (!r.after)
            continue;
        const by = r.hasActor
            ? 'segreteria'
            : r.action.endsWith('.bot')
                ? 'assistente'
                : 'altro';
        for (const field of CAMPI_DELLO_STORICO) {
            if (!(field in r.after))
                continue;
            const parola = (v) => field === 'gender' && typeof v === 'string' ? (SESSO_A_PAROLE[v] ?? v) : comeTesto(v);
            const after = parola(r.after[field]);
            const before = parola(r.before?.[field]);
            if (before === after)
                continue;
            righe.push({ at: r.at, field, before, after, by, actorName: r.hasActor ? r.actorName : null });
        }
    }
    return righe.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}
// ─── 📱 La richiesta in «💬 Segreteria» quando il numero è di un altro ──────
/** Cosa sta in `member_feedback.request` per `numero_occupato`. */
export const richiestaNumeroOccupatoSchema = z.object({
    phone: z.string(),
    otherMemberId: z.uuid(),
});
/**
 * Il testo della richiesta, detto dal socio: è quello che la Segreteria gli
 * cita rispondendo («Avevi scritto: …»). La pagina della Segreteria la racconta
 * con le due schede, da `numero`.
 */
export function testoNumeroOccupato(phone) {
    return `Aggiornare il mio numero a ${phone}.`;
}
//# sourceMappingURL=i-miei-dati.js.map